/**
 * Exchange Rate Configuration
 *
 * USD-BASE PRICING (cutover 2026-06-19)
 * --------------------------------------
 * All store values (wholesale, retail, wallet, CDR cost) are USD.
 * The historical AUD-named fields/functions are kept for back-compat —
 * 1 "AUD cent" in code === 1 USD cent at runtime. Display layer converts
 * USD → user's local currency via DISPLAY_CURRENCIES.rate (USD→local).
 *
 * GST is NOT applied to any pricelist, including Australia. Australian
 * users see "A$" labels powered by AU.rate (USD→AUD ≈ 1.55) with no
 * GST line item.
 *
 * Sources of truth on the box:
 *   - scripts/rate-ingest/outbound-wholesale-max.txt  (comparator guard)
 *   - scripts/rate-ingest/carrier-overrides.yaml      (carrier pins)
 *   - ASTPP routes pl=4 (IDT) / pl=5 (DIDWW)          (USD wholesale)
 *   - ASTPP routes pl=2 / pl=3                        (USD retail post-cutover)
 */

export const EXCHANGE_RATES = {
  // Legacy name retained for back-compat. Store currency is now USD, so
  // any code that multiplies a USD wholesale cent by this gets the same
  // USD cent back. Display-side conversion lives in DISPLAY_CURRENCIES.
  USD_TO_AUD: 1.00,

  LAST_UPDATED: '2026-06-19',
  UPDATE_FREQUENCY: 'manual (cutover constant)',
};

/**
 * Margin configuration for rate calculations.
 *
 * BASE_MARGIN was 1.45 in AUD-inc-GST world, where effective stack was
 *   retail_aud_inc_gst = wholesale_usd × 1.55 (fx) × 1.45 (margin) × 1.10 (gst)
 *                      = wholesale_usd × 2.473
 * Holding revenue flat in USD-base (no FX, no GST):
 *   retail_usd = wholesale_usd × 1.60   →   shown to AU as 1.60 × 1.55 = A$2.48
 * Mirror this number into build-retail-lcr.py / build-lcr.py --margin so
 * the server-side LCR build agrees with the client-side calculator.
 */
export const RATE_MARGINS = {
  BASE_MARGIN: 1.60,
  MOBILE_CUSTOMER_DISCOUNT: 0.15,
  BUNDLE_CUSTOMER_DISCOUNT: 0.25,
};

/**
 * GST Configuration — DISABLED at cutover. Kept for back-compat with any
 * caller still importing GST_CONFIG. INCLUDE_IN_RETAIL=false means
 * calculateRetailRate() no longer multiplies by MULTIPLIER.
 */
export const GST_CONFIG = {
  RATE: 0.00,
  MULTIPLIER: 1.00,
  INCLUDE_IN_RETAIL: false,
};

/**
 * List of countries that are included in unlimited calling bundles
 * These countries should NOT appear in per-minute rate searches
 */
export const UNLIMITED_COUNTRIES = [
  { code: '61', name: 'Australia' },
  { code: '64', name: 'New Zealand' },
  { code: '44', name: 'United Kingdom' },
  { code: '1', name: 'USA & Canada' },
  { code: '33', name: 'France' },
  { code: '49', name: 'Germany' },
  { code: '39', name: 'Italy' },
  { code: '34', name: 'Spain' },
  { code: '353', name: 'Ireland' },
  { code: '31', name: 'Netherlands' },
  { code: '65', name: 'Singapore' },
  { code: '852', name: 'Hong Kong' },
  { code: '81', name: 'Japan' },
  { code: '82', name: 'South Korea' },
];

export const getUnlimitedCountryCodes = (): string[] =>
  UNLIMITED_COUNTRIES.map(c => c.code);

export const isUnlimitedCountry = (countryCode: string): boolean =>
  getUnlimitedCountryCodes().includes(countryCode);

/**
 * Calculate retail rate from USD wholesale cost.
 * Post-cutover: store currency = USD, no GST, no FX.
 *
 * @param usdCents wholesale USD cents
 * @returns retail USD cents
 */
export const calculateRetailRate = (usdCents: number): number => {
  return Math.round(usdCents * RATE_MARGINS.BASE_MARGIN);
};

export const calculateMobileCustomerRate = (retailRateCents: number): number =>
  Math.round(retailRateCents * (1 - RATE_MARGINS.MOBILE_CUSTOMER_DISCOUNT));

export const calculateBundleCustomerRate = (retailRateCents: number): number =>
  Math.round(retailRateCents * (1 - RATE_MARGINS.BUNDLE_CUSTOMER_DISCOUNT));

/**
 * Display currency configuration for geo-localized rate display.
 *
 * `rate` semantics changed at the USD-base cutover:
 *   rate = USD → local currency multiplier
 *   localCents = usdCents × rate
 *
 * `showGst` is false everywhere now — GST was removed from all pricelists
 * including Australia. Field is kept for back-compat with UI badges that
 * conditionally render a "GST inc." chip.
 */
export interface DisplayCurrency {
  code: string;
  symbol: string;
  rate: number;  // USD -> local multiplier
  showGst: boolean;
  name: string;
}

export const DISPLAY_CURRENCIES: Record<string, DisplayCurrency> = {
  US: { code: 'USD', symbol: 'US$', rate: 1.00, showGst: false, name: 'US Dollars' },
  AU: { code: 'AUD', symbol: 'A$',  rate: 1.55, showGst: false, name: 'Australian Dollars' },
  GB: { code: 'GBP', symbol: '£',   rate: 0.80, showGst: false, name: 'British Pounds' },
  CA: { code: 'CAD', symbol: 'CA$', rate: 1.38, showGst: false, name: 'Canadian Dollars' },
  NZ: { code: 'NZD', symbol: 'NZ$', rate: 1.67, showGst: false, name: 'New Zealand Dollars' },
  DE: { code: 'EUR', symbol: '€',   rate: 0.92, showGst: false, name: 'Euros' },
  FR: { code: 'EUR', symbol: '€',   rate: 0.92, showGst: false, name: 'Euros' },
  NL: { code: 'EUR', symbol: '€',   rate: 0.92, showGst: false, name: 'Euros' },
  BE: { code: 'EUR', symbol: '€',   rate: 0.92, showGst: false, name: 'Euros' },
  IT: { code: 'EUR', symbol: '€',   rate: 0.92, showGst: false, name: 'Euros' },
  IE: { code: 'EUR', symbol: '€',   rate: 0.92, showGst: false, name: 'Euros' },
  AE: { code: 'AED', symbol: 'AED ',rate: 3.67, showGst: false, name: 'UAE Dirhams' },
};

export const DEFAULT_DISPLAY_CURRENCY: DisplayCurrency = DISPLAY_CURRENCIES.US;

/**
 * Referral reward amount in local currency minor units.
 * Always "5.00" in whatever the user's local currency is.
 */
export const REFERRAL_REWARD_LOCAL_CENTS = 500;

/**
 * Convert a local currency amount back to store-currency cents (USD post-cutover).
 * Function name retained for back-compat — "AudCents" now means "store cents" = USD cents.
 *
 *   storeCents = localCents / rate
 *
 * Examples (post-cutover):
 *   localCurrencyToAudCents(500, 'US') => 500   (500 / 1.00)
 *   localCurrencyToAudCents(500, 'AU') => 323   (500 / 1.55)
 *   localCurrencyToAudCents(500, 'GB') => 625   (500 / 0.80)
 */
export const localCurrencyToAudCents = (localCents: number, countryISO2: string): number => {
  const currency = DISPLAY_CURRENCIES[countryISO2] || DEFAULT_DISPLAY_CURRENCY;
  return Math.round(localCents / currency.rate);
};

export const getDisplayCurrencyForCountry = (countryISO2: string): DisplayCurrency =>
  DISPLAY_CURRENCIES[countryISO2] || DEFAULT_DISPLAY_CURRENCY;

